import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { pipe, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ReportsApiService } from '../../reports/reports.apiservice';
import { CampaignsApiService } from '../campaigns.apiservice';
import { OrdersApiService } from '../../orders/orders.apiservice';
import { SubscriptionApiService } from '../../subscriptions/subscription.apiservice';
import { ICampaign } from '../../../core/models/campaigns.model';
import { IReportingPeriod } from '../../../core/models/reporting.models';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-campaign-budget-audit',
  templateUrl: './campaign-budget-audit.component.html',
  styleUrls: ['./campaign-budget-audit.component.scss']
})
export class CampaignBudgetAuditComponent implements OnInit {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  // ordersData: Array<any> = [];
  productsData: Array<any> = [];
  aggregateData: Array<any> = [];
  budgetTotals: any = {
    date: '',
    cartDiscount: 0,
    refundAmount: 0,
    proratePrice: 0,
    couponAmount: 0,
    totalPrice: 0,
    cost: 0,
    netAmount: 0,
    percentDifference: 0
  };
  campaignData: ICampaign = {} as ICampaign;
  budgetForm: FormGroup;
  reportMetrics: any = {};
  reportPeriods: Array<IReportingPeriod> = [];
  DateStart: string | null = null;
  DateStop: string | null = null;
  selectedPeriod: string | null = null;
  platform = '';

  private normalizePeriodValueToIsoDate(value: string): string {
    if (!value) {
      return value;
    }

    const trimmed = String(value).trim();
    if (/^\d{4}-\d{2}$/.test(trimmed)) {
      return `${trimmed}-01`;
    }

    return trimmed;
  }
  
  campaignId: string = '';
  @Input() set iCampaignId(id: string) {
    this.campaignId = id;
  }
  @Input() iLayout: string = 'column'; // column or row

  budgetColumns = [
    { title: 'MONTH', field: 'date', sortable: true, pipe: 'P_AppDateFormat', pipeArgs: ['MMM yyyy'] },
    { title: 'PAID', field: 'totalPrice', sortable: true, pipe: 'Currency', pipeArgs: ['USD', 'symbol', '1.2-2'] },
    { title: 'SPENT', field: 'cost', sortable: true, pipe: 'Currency', pipeArgs: ['USD', 'symbol', '1.2-2'] },
    { title: 'DIFFERENCE', field: 'netAmount', sortable: true, pipe: 'Currency', pipeArgs: ['USD', 'symbol', '1.2-2'], hoverValue: 'dailyBudget' },
    { title: '  ', field: '' },
    { title: 'ORDER_AMOUNT', field: 'proratePrice', sortable: true, pipe: 'Currency', pipeArgs: ['USD', 'symbol', '1.2-2'] },
    { title: 'PRODUCT_COUPONS', field: 'couponAmount', sortable: true, pipe: 'Currency', pipeArgs: ['USD', 'symbol', '1.2-2'] },
    { title: 'CART_COUPON_AMOUNT', field: 'cartDiscount', sortable: true, pipe: 'Currency', pipeArgs: ['USD', 'symbol', '1.2-2'] },
    { title: 'ORDER_REFUND_AMOUNT', field: 'refundAmount', sortable: true, pipe: 'Currency', pipeArgs: ['USD', 'symbol', '1.2-2'] },
  ];

  get visibleBudgetColumns(): Array<any> {
    const hasCouponAmount = this.columnHasNonZeroData('couponAmount');
    const hasCartDiscount = this.columnHasNonZeroData('cartDiscount');
    const hasRefundAmount = this.columnHasNonZeroData('refundAmount');
    const showOrderAdjustments = hasCouponAmount || hasCartDiscount || hasRefundAmount;

    return (this.budgetColumns || []).filter(col => {
      const isSpacerColumn = col?.field === '' && ((col?.title ?? '').trim() === '');
      switch (col?.field) {
        case 'proratePrice':
          return showOrderAdjustments;
        case 'couponAmount':
          return hasCouponAmount;
        case 'cartDiscount':
          return hasCartDiscount;
        case 'refundAmount':
          return hasRefundAmount;
        default:
          if (isSpacerColumn) {
            return showOrderAdjustments;
          }
          return true;
      }
    });
  }

  private columnHasNonZeroData(field: string): boolean {
    const data = this.aggregateData || [];
    for (const row of data) {
      const value = Number((row as any)?.[field] ?? 0);
      if (Number.isFinite(value) && Math.abs(value) > 0) {
        return true;
      }
    }

    const totalValue = Number((this.budgetTotals as any)?.[field] ?? 0);
    return Number.isFinite(totalValue) && Math.abs(totalValue) > 0;
  }

  constructor(
    private ordersApiService: OrdersApiService,
    private reportsApiService: ReportsApiService,
    private subscriptionService: SubscriptionApiService,
    private campaignService: CampaignsApiService,
    private formBuilder: FormBuilder,
    private translate: TranslateService
  ) {
    this.budgetForm = this.formBuilder.group({
      key: [null, [Validators.required]],
      startDate: [null, [Validators.required]],
      stopDate: [null, [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.loadCampaignDetails(this.campaignId);
  }

  loadCampaignDetails(id: string): void {
    this.campaignService.getCampaignDetails(id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.campaignData = response.data;
        // console.log('Campaign Data:', this.campaignData);
        this.platform = this.campaignData.platform;
        this.loadReportPeriods();
      });
  }

  async loadReportPeriods() {
    this.reportPeriods = [];
    await this.getSupportedReportingPeriods();

    this.DateStart = this.reportPeriods.find(f => f.key == 'LAST_MONTH')?.startDate || '';
    this.DateStop = this.reportPeriods.find(f => f.key == 'LAST_MONTH')?.stopDate || '';
    this.selectedPeriod = 'LAST_MONTH';

    this.budgetForm.patchValue({
      key: this.selectedPeriod,
      startDate: this.DateStart,
      stopDate: this.DateStop,
    });

    // await this.getReportData();
  }

  getSupportedReportingPeriods(): Promise<void> {
    return new Promise((resolve) => {
      const today = new Date();
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, 1);
      const sixMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 6, 1);
      const startOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const startOfThisYear = new Date(today.getFullYear(), 0, 1);
      const startOfLastYear = new Date(today.getFullYear() - 1, 0, 1);
      const endOfLastYear = new Date(today.getFullYear() - 1, 11, 31);

      this.reportPeriods.push({ key: 'LAST_MONTH', startDate: `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`, stopDate: `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}` });
      this.reportPeriods.push({ key: 'THIS_MONTH', startDate: `${startOfThisMonth.getFullYear()}-${String(startOfThisMonth.getMonth() + 1).padStart(2, '0')}`, stopDate: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}` });
      this.reportPeriods.push({ key: 'LAST_3_MONTHS', startDate: `${threeMonthsAgo.getFullYear()}-${String(threeMonthsAgo.getMonth() + 1).padStart(2, '0')}`, stopDate: `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}` });
      this.reportPeriods.push({ key: 'LAST_6_MONTHS', startDate: `${sixMonthsAgo.getFullYear()}-${String(sixMonthsAgo.getMonth() + 1).padStart(2, '0')}`, stopDate: `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}` });
      this.reportPeriods.push({ key: 'THIS_YEAR', startDate: `${startOfThisYear.getFullYear()}-${String(startOfThisYear.getMonth() + 1).padStart(2, '0')}`, stopDate: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}` });
      this.reportPeriods.push({ key: 'LAST_YEAR', startDate: `${startOfLastYear.getFullYear()}-${String(startOfLastYear.getMonth() + 1).padStart(2, '0')}`, stopDate: `${endOfLastYear.getFullYear()}-${String(endOfLastYear.getMonth() + 1).padStart(2, '0')}` });

      resolve();
    });
  }

  onReportPeriodChange(selectedPeriod: string) {
    let period = this.reportPeriods.find(f => f.key == selectedPeriod);
    this.DateStart = period?.startDate as string;
    this.DateStop = period?.stopDate as string;

    this.budgetForm.patchValue({
      key: selectedPeriod,
      startDate: this.DateStart,
      stopDate: this.DateStop,
    });
  }
  get selectedReportPeriod(): string | undefined | null { return this.budgetForm?.controls?.key?.value; }
  // get datesReadonly(): boolean { return !(this.selectedReportPeriod == 'Custom'); }

  submitBudgetForm() {
    if (this.budgetForm.invalid) {
      return;
    }
    this.DateStart = this.budgetForm.get('startDate')?.value;
    this.DateStop = this.budgetForm.get('stopDate')?.value;
    this.selectedPeriod = this.budgetForm.get('key')?.value;

    this.loadOrders();
    this.getReportData();
  }

  async loadOrders(): Promise<void> {
    // this.ordersData = [];
    this.productsData = [];
    this.subscriptionService.getAllSubscriptions(this.campaignData.id, this.campaignData.clientId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        // console.log('Subscriptions', response);
        if (response.subscriptions) {
          response.subscriptions.forEach((subscription: any) => {
            this.subscriptionService.getSubscription(subscription.id)
              .pipe(takeUntil(this.destroyed$))
              .subscribe((response: any) => {
                // console.log('Subscription', response);
                if (response && response.subscription.orders) {
                  response.subscription.orders.forEach((order: any) => {
                    if (order.paid && order.isActive) {
                      this.ordersApiService.getShopOrderDetails(order.id)
                      .pipe(takeUntil(this.destroyed$))
                      .subscribe((orderDetails: any) => {
                        // this.ordersData.push(orderDetails.data);
                        orderDetails.data.products.forEach((product: any) => {
                          let productPush: any = {};
                          if (product.type === 3) {      
                            productPush.subscriptionNumericId = subscription.numericId;
                            productPush.subscriptionId = subscription.id;

                            productPush.orderNumber = orderDetails.data.orderNumber;
                            productPush.orderId = orderDetails.data.id;
                            productPush.cartCoupon = orderDetails.data.cartCoupon;
                            productPush.cartDiscount = orderDetails.data.cartDiscount || 0;
                            productPush.refundAmount = orderDetails.data.refundAmount || 0;
                            productPush.orderDate = orderDetails.data.orderDate;

                            productPush.productName = product.display;
                            productPush.proratePrice = product.proratePrice || 0;
                            productPush.couponAmount = product.couponAmount || 0;
                            productPush.totalPrice = product.proratePrice - product.couponAmount;
                            productPush.couponCodes = product.couponCodes;
                            
                            if (this.campaignData.platform == "Facebook" && product.isFacebook) {
                              this.productsData.push(productPush);
                            } else if ((this.campaignData.platform == "Google" || this.campaignData.platform == "National Add On") && product.isGoogle) {
                              this.productsData.push(productPush);
                            }
                          }
                        });
                      });
                    }
                  });
                }
              });
          });
        }
      });
    // console.log('Campaign', this.campaignData);
    // console.log('Orders', this.ordersData);
    // console.log('Products', this.productsData);
  }

  async getReportData(): Promise<void> {
    let reachCampaignID = this.campaignData.id;
    this.reportMetrics = [];

    if (this.campaignData.platform === 'Google' || this.campaignData.platform === 'Facebook') {
      let PerformanceMetrics = '7';

      if (reachCampaignID && this.campaignData.platform && this.DateStart && this.DateStop) {
        const startDateValue = this.normalizePeriodValueToIsoDate(this.DateStart);
        const endDateValue = this.normalizePeriodValueToIsoDate(this.DateStop);
        const startDate = new Date(startDateValue + 'T00:00:00');
        const endDate = new Date(endDateValue + 'T23:59:59');

        for (let date = new Date(startDate); date <= endDate;) {
          const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
          const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);

          await this.reportsApiService.getRawCampaignsData(reachCampaignID, this.campaignData.platform, monthStart.toISOString().split('T')[0], monthEnd.toISOString().split('T')[0], PerformanceMetrics)
            .pipe(takeUntil(this.destroyed$))
            .toPromise()
            .then((data: any) => {
              // console.log('Raw Campaigns Data:', data);
              if (!data.ErrorMessage && data.Platform) {
                this.reportMetrics.push({
                  noData: '',
                  Platform: data.Platform.trim(),
                  DateRange: data.GraphDateRange.trim(),
                  Cost: data.Cost,
                  Leads: data.Conversions,
                  Month: `${monthEnd.getFullYear()}-${monthEnd.getMonth() + 1}-1`
                });
              } else {
                this.reportMetrics.push({
                  noData: this.translate.instant('NO_DATA_FOUND_FOR_SELECTED_CAMPAIGN_OR_DATE_RANGE'),
                  Platform: '',
                  DateRange: '',
                  Cost: 0,
                  Leads: 0,
                  Month: `${monthEnd.getFullYear()}-${monthEnd.getMonth() + 1}-1`
                });
              }
            })
            .catch((error: any) => {
              console.error('Error fetching raw campaigns data:', error);
              this.reportMetrics.push({
                noData: this.translate.instant('ERROR_FETCHING_DATA'),
                Platform: '',
                DateRange: '',
                Cost: 0,
                Month: `${monthStart.getFullYear()}-${monthStart.getMonth() + 1}-1`
              });
            });

          date.setMonth(date.getMonth() + 1);
          date.setDate(1); // Reset the date to the first of the month
        }

        // console.log('Report Data:', this.reportMetrics);
      }
    } else {
      console.error(this.translate.instant('INVALID_PLATFORM'), this.campaignData.platform);
    }

    // this.aggregateBudgetData();
    this.aggregateDataByMonthYear();
  } 

  aggregateDataByMonthYear() {
    const groupedProductsData: { [key: string]: any } = {};
    const groupedReportMetrics: { [key: string]: any } = {};
    this.aggregateData = [];

    const startDateValue = this.normalizePeriodValueToIsoDate(this.DateStart || '');
    const endDateValue = this.normalizePeriodValueToIsoDate(this.DateStop || '');
    const startDate = new Date(startDateValue + 'T00:00:00');
    const endDate = new Date(endDateValue + 'T23:59:59');
    endDate.setMonth(endDate.getMonth() + 1);
    endDate.setDate(0);

    this.productsData.forEach(product => {
      const date = new Date(product.orderDate);
      const monthYear = `${date.getFullYear()}-${date.getMonth() + 1}`;

      if (date >= startDate && date <= endDate) {
        if (!groupedProductsData[monthYear]) {
          groupedProductsData[monthYear] = {
            monthYear,
            cartDiscount: 0,
            refundAmount: 0,
            proratePrice: 0,
            couponAmount: 0,
            totalPrice: 0,
            cost: 0,
            netAmount: 0
          };
        }

        groupedProductsData[monthYear].cartDiscount += product.cartDiscount;
        groupedProductsData[monthYear].refundAmount += product.refundAmount;
        groupedProductsData[monthYear].proratePrice += product.proratePrice;
        groupedProductsData[monthYear].couponAmount += product.couponAmount;
        groupedProductsData[monthYear].totalPrice += product.totalPrice;
        // groupedProductsData[monthYear].date = date.toISOString();
      }
    });

    this.reportMetrics.forEach((report: any) => {
      const date = new Date(report.Month);
      const monthYear = `${date.getFullYear()}-${date.getMonth() + 1}`;

      if (date >= startDate && date <= endDate) {
        if (!groupedReportMetrics[monthYear]) {
          groupedReportMetrics[monthYear] = {
            monthYear,
            cost: 0,
            netAmount: 0,
            percentDifference: 0
          };
        }

        groupedReportMetrics[monthYear].date = date.toISOString();
        groupedReportMetrics[monthYear].cost = report.Cost;
        groupedReportMetrics[monthYear].netAmount = report.Cost - (groupedProductsData[monthYear]?.totalPrice || 0);
        groupedReportMetrics[monthYear].percentDifference = groupedReportMetrics[monthYear].netAmount / (groupedProductsData[monthYear]?.totalPrice || 1);
        if (!(groupedReportMetrics[monthYear].percentDifference < 0.03 && groupedReportMetrics[monthYear].percentDifference > -0.03)) {
          groupedReportMetrics[monthYear].statusColor = 'red';
        }
        if (date.getMonth() === new Date().getMonth() && date.getFullYear() === new Date().getFullYear()) {
          const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
          const today = new Date().getDate();
          const daysLeftInMonth = daysInMonth - today + 1; // Include Today
          groupedReportMetrics[monthYear].dailyBudget = ((groupedProductsData[monthYear]?.totalPrice - report.Cost) / daysLeftInMonth) || 0;
        } else {
          groupedReportMetrics[monthYear].dailyBudget = null;
        }
        
      }
    });

    const allMonthYears = new Set([...Object.keys(groupedProductsData), ...Object.keys(groupedReportMetrics)]);

    allMonthYears.forEach(monthYear => {
      this.aggregateData.push({
        ...groupedProductsData[monthYear],
        ...groupedReportMetrics[monthYear],
        monthYear
      });
    });

    this.aggregateData.sort((b, a) => {
      const dateA = new Date(a.date || `${a.monthYear}-01`);
      const dateB = new Date(b.date || `${b.monthYear}-01`);
      return dateA.getTime() - dateB.getTime();
    });

    this.budgetTotals = this.getTotals(this.aggregateData);
    this.budgetTotals.percentDifference = (this.budgetTotals.netAmount / this.budgetTotals.totalPrice) || 0;
    // console.log('Aggregate Data', this.aggregateData);
    // console.log('Budget Totals', this.budgetTotals);
  }

  getTotals(data: Array<any>): any {
    const totals: any = {};

    data.forEach(item => {
      Object.keys(item).forEach(key => {
        if (typeof item[key] === 'number') {
          if (!totals[key]) {
            totals[key] = 0;
          }
          totals[key] += item[key];
        }
      });
    });

    return totals;
  }
}
